import "./infobox.scss";

import { ReactNode } from "react";
import { MediaAssetImageProps } from "design-system/components/primitives/media-asset/media-asset-image";
import { MediaAsset } from "design-system/components/primitives/media-asset/media-asset";
import {
  CtaLink,
  CtaLinkProps,
} from "design-system/components/primitives/cta-link/cta-link";
import { SimpleTheme } from "design-system/utils/theme";
import { Link } from "design-system/components/primitives/link/link";
import { Alignment } from "design-system/types/types";
import { DataLayerEvent, useDataLayer } from "frontend/hooks/use-data-layer";

export interface InfoboxProps {
  type: string;
  name: string;
  image?: MediaAssetImageProps;
  intro?: string;
  descriptionLabel?: string;
  description?: ReactNode;
  link?: CtaLinkProps;
  secondaryLink?: CtaLinkProps;
  recentPublications?: Array<CtaLinkProps>;
  metadata?: Array<metaItem>;
  theme?: SimpleTheme;
  align?: Alignment;
  url?: string;
  dataLayer?: DataLayerEvent;
}

interface metaItem {
  label: string;
  description: ReactNode;
}

export function InfoBox({
  type,
  name,
  image,
  intro,
  descriptionLabel,
  description,
  link,
  secondaryLink,
  recentPublications,
  metadata,
  align = "full",
  theme = "light",
  url,
  dataLayer,
}: InfoboxProps) {
  const { pushDataLayer } = useDataLayer();
  const hideInfoSection = !intro && !description ? true : false;
  const movedLink = hideInfoSection;
  const imageClass = hideInfoSection
    ? "hbs-infobox__enlarged_image"
    : "hbs-infobox__image";
  let handleOnClick = undefined;
  if (dataLayer) {
    handleOnClick = () => {
      /* eslint-disable camelcase */
      if (dataLayer?.event_data.explore_search) {
        dataLayer.event_data.explore_search_info_box_clicked = name;
      }
      if (dataLayer) pushDataLayer(dataLayer);
      /* eslint-enable camelcase */
    };
  }
  if (!dataLayer) {
    dataLayer = {
      /* eslint-disable camelcase */
      event: "info_box_item_click",
      event_data: {
        infobox_category: type,
        infobox_name: name,
      },
      /* eslint-enable camelcase */
    };
  }
  return (
    <div className={`hbs-infobox hbs-global-align-${align}`} data-theme={theme}>
      <div className="hbs-infobox__header">
        {image?.src && (
          <div className={imageClass}>
            <MediaAsset image={image} />
          </div>
        )}
        <div className="hbs-infobox__header-content">
          <p className="hbs-infobox__type">{type} Snapshot</p>
          {name && (
            <h1 className="hbs-infobox__title">
              {url ? (
                <Link href={url} dataLayer={dataLayer} onClick={handleOnClick}>
                  {name}
                </Link>
              ) : (
                name
              )}
            </h1>
          )}
          {movedLink && link && (
            <CtaLink
              {...link}
              className="hbs-infobox__link"
              isSmall
              type="primary-button"
              dataLayer={dataLayer}
            />
          )}
        </div>
      </div>
      <div className="hbs-infobox__content">
        {!hideInfoSection && (
          <div className="hbs-infobox__section">
            {intro && <div className="hbs-infobox__section-intro">{intro}</div>}
            {description && (
              <div className="hbs-infobox__section-content">
                {descriptionLabel && (
                  <p className="hbs-infobox__section-head">
                    {descriptionLabel}
                  </p>
                )}
                <div className="hbs-infobox__section-description">
                  {description}
                </div>
              </div>
            )}
            <div className="hbs-infobox-ctas">
              {link && (
                <CtaLink
                  {...link}
                  className="hbs-infobox__link"
                  isSmall
                  type="primary-button"
                  dataLayer={dataLayer}
                />
              )}

              {secondaryLink && (
                <CtaLink
                  {...secondaryLink}
                  className="hbs-infobox__secondaryLink"
                  isSmall
                  type="secondary-button"
                  icon="arrow"
                  dataLayer={dataLayer}
                />
              )}
            </div>
          </div>
        )}

        {recentPublications ? (
          <div className="hbs-infobox__section">
            <p className="hbs-infobox__section-head">Recent Publications</p>
            <ul>
              {recentPublications.map((item, i) => (
                <li key={i} className="hbs-infobox__recent-publication-item">
                  <Link href={item.href}> {item.children}</Link>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          metadata && (
            <div className="hbs-infobox__section hbs-infobox__metadata">
              {metadata.map((item, i) => (
                <dl key={i} className="hbs-infobox__metadata-item">
                  <dt className="hbs-infobox__section-head">{item.label}</dt>
                  <dd className="hbs-infobox__metadata-description">
                    {item.description}
                  </dd>
                </dl>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
